import { allNavItems } from '@/config/navigation'
import menuLabels from '@/i18n/navigation.json'
import type { SessionStore } from '@/stores/SessionStore'
import { isVisible } from './access'
import type { MenuArea, MenuEntry, MenuKey, MenuNames, MenuNode } from './legacyMenu'

/**
 * Visibility and active-route rules of the legacy menu, shared by the side
 * menu and the three-row top menu so both always agree on what is shown.
 */

export type Locale = keyof (typeof menuLabels)[MenuKey]

/**
 * A menu label: a node's own names (the server's menu rows), or a label key
 * of navigation.json (the app's own labels and the keys recent pages stored).
 */
export function menuText(source: string | { label: string; names?: MenuNames }, locale: Locale) {
  if (typeof source !== 'string' && source.names) return source.names[locale] || source.names.en
  const key = (typeof source === 'string' ? source : source.label) as MenuKey
  return menuLabels[key]?.[locale] || menuLabels[key]?.en || key
}

/** Listed pages by path; detail routes are hidden and never a menu target. */
export const itemsByPath = new Map(allNavItems.filter((item) => !item.hidden).map((item) => [item.path, item]))

/** Pages joined into another one: the server menu still lists them, the app drops them («Приёмка филиалом», client #299). */
const retiredPaths = new Set(allNavItems.filter((item) => item.retired).map((item) => item.path))

export type FlatEntry = { entry: MenuEntry; trail: string[] }

/** Every entry in menu order with the ids of the area and folders above it. */
export function flatten(nodes: MenuNode[], trail: string[], out: FlatEntry[] = []) {
  for (const node of nodes) {
    if (node.kind === 'folder') {
      flatten(node.children, [...trail, node.id], out)
    } else {
      out.push({ entry: node, trail })
      if (node.children) flatten(node.children, trail, out)
    }
  }
  return out
}

export const flatEntries = (areas: MenuArea[]) => areas.flatMap((area) => flatten(area.children, [area.id]))

/** The server lists only entries the profile may see; the page itself may still be hidden or planned here. */
export function entryVisible(entry: MenuEntry, session: SessionStore) {
  if (retiredPaths.has(entry.path)) return false
  const item = itemsByPath.get(entry.path)
  // No page yet: listed locked, like a planned page.
  if (!item) return true
  return isVisible(item, session)
}

export function nodeVisible(node: MenuNode, session: SessionStore): boolean {
  if (node.kind === 'folder') return node.children.some((child) => nodeVisible(child, session))
  return entryVisible(node, session) || Boolean(node.children?.some((child) => entryVisible(child, session)))
}

const pathMatches = (path: string, pathname: string) => pathname === path || pathname.startsWith(`${path}/`)

function searchMatches(search: string, current: URLSearchParams) {
  return [...new URLSearchParams(search)].every(([key, value]) => current.get(key) === value)
}

/**
 * An entry is active on its page. Entries that share a page split it by view:
 * a view entry needs its search parameters, the plain entry takes the rest.
 */
export function entryActive(entry: MenuEntry, pathname: string, current: URLSearchParams, areas: MenuArea[]) {
  if (!pathMatches(entry.path, pathname)) return false
  if (entry.search) return searchMatches(entry.search, current)
  return !flatEntries(areas).some(({ entry: other }) => other.path === entry.path && other.search && searchMatches(other.search, current))
}

/** Menu order decides which of several matching entries (role Orders menus) owns the route. */
export function findActiveEntry(session: SessionStore, isActive: (entry: MenuEntry) => boolean) {
  return flatEntries(session.menuAreas).find(({ entry }) => entryVisible(entry, session) && isActive(entry))
}

/** Whether `target` sits at or below `node`. */
export function nodeContains(node: MenuNode, target: MenuEntry): boolean {
  if (node === target) return true
  return Boolean(node.children?.some((child) => nodeContains(child, target)))
}
